import type {
  PreflightBlocker,
  PreflightResult,
  PreflightWarning,
  PreflightSettingPass,
} from '@ctcells/contract'
import { enumLabel, hasMessage, t } from '@ctcells/i18n'

import { Disclosure } from '../../atoms/Disclosure.tsx'
import { StatusBadge } from '../../atoms/StatusBadge.tsx'
import { errorMessageOf } from '../format.ts'
import { MODULE_GROUPS, moduleGroupLabel, type ModuleGroup } from '../moduleGroups.ts'
import { Card } from '../resourceParts.tsx'
import type { PreflightRowExtraFn } from './PreflightReport.tsx'

import '../../atoms/table.css'
import './groupedPreflightReport.css'

type Row = PreflightBlocker | PreflightWarning
type Group = 'fail' | 'inconclusive' | 'warning'
/* 묶음 이름 · 차례는 알람별 전달 표와 한 곳(`moduleGroups.ts`) */
const CATEGORIES = MODULE_GROUPS
type Category = ModuleGroup
const MODULE_CATEGORIES = new Map<string, Category>([
  ['cent', 'cent'],
  ['centrifuge', 'cent'],
  ['asm', 'asm'],
  ['assembler', 'asm'],
  ['stain', 'stain'],
  ['staining', 'stain'],
  ['robot', 'robot'],
  ['robot6', 'robot'],
])
const REAGENT_CODES = new Set([
  'REAGENT_SHORTAGE_AT_START',
  'REAGENT_VOLUME_LOW',
  'REAGENT_EXPIRED_AT_START',
  'REAGENT_TEMP_NOT_REACHED',
  'REAGENT_LOT_NOT_REGISTERED',
])

function categoryOf(row: Row): Category {
  if (row.code === 'BATCH_SAMPLE_SET_UNSUPPORTED') return 'common'
  const itemPrefix = row.item?.split('.')[0]
  if (REAGENT_CODES.has(row.code) || itemPrefix === 'reagents') return 'reagents'
  const source =
    row.module_id == null ? itemPrefix : row.module_id.replace(/^module:/, '').split(/[:-]/)[0]
  return (source === undefined ? undefined : MODULE_CATEGORIES.get(source)) ?? 'common'
}

const categoryLabel = moduleGroupLabel

function problemOf(row: Row): string {
  const key = `preflight.issue.${row.code}`
  return hasMessage(key) ? t(key) : errorMessageOf(row.code, row.code)
}

function settingFor(row: Row, passedBySetting: PreflightResult['passed_by_setting']) {
  return passedBySetting?.find(
    (entry) =>
      entry.code === row.code &&
      (entry.module_id ?? null) === (row.module_id ?? null) &&
      (entry.item ?? null) === (row.item ?? null),
  )
}

export function GroupedPreflightReport({
  result,
  rowExtra,
  hideTitle = false,
}: {
  result: PreflightResult
  rowExtra?: PreflightRowExtraFn | undefined
  hideTitle?: boolean | undefined
}) {
  const resultBadge = (
    <span className="pfres__head">
      <StatusBadge
        label={result.passed ? t('preflight.passed') : t('preflight.blocked')}
        status={result.passed ? 'completed' : 'error'}
      />
    </span>
  )
  return (
    <div className="pfres pfgroups">
      <Card title={hideTitle ? undefined : t('preflight.matrix.title')} titleEnd={resultBadge}>
        {hideTitle ? <div className="card__title">{resultBadge}</div> : null}
        <PreflightMatrix
          blockers={result.blockers}
          warnings={result.warnings}
          passedBySetting={result.passed_by_setting}
          rowExtra={rowExtra}
        />
      </Card>
    </div>
  )
}

export function GroupedWarningList({
  warnings,
  rowExtra,
  hideTitle = false,
}: {
  warnings?: PreflightWarning[] | undefined
  rowExtra?: PreflightRowExtraFn | undefined
  hideTitle?: boolean | undefined
}) {
  return (
    <div className="pfgroups">
      <Card title={hideTitle ? undefined : t('preflight.matrix.title')}>
        <PreflightMatrix warnings={warnings} rowExtra={rowExtra} />
      </Card>
    </div>
  )
}

function PreflightMatrix({
  blockers,
  warnings,
  passedBySetting,
  rowExtra,
}: {
  blockers?: PreflightBlocker[] | undefined
  warnings?: PreflightWarning[] | undefined
  passedBySetting?: PreflightResult['passed_by_setting'] | undefined
  rowExtra?: PreflightRowExtraFn | undefined
}) {
  const remaining = blockers?.filter((row) => settingFor(row, passedBySetting) === undefined)
  const groups: { kind: Group; rows: Row[] | undefined }[] = [
    { kind: 'fail', rows: remaining?.filter((row) => row.verdict === 'fail') },
    { kind: 'inconclusive', rows: blockers?.filter((row) => row.verdict === 'inconclusive') },
    { kind: 'warning', rows: warnings },
  ]
  return (
    <div className="pfmatrix">
      <div
        className="pfmatrix__scroll"
        role="region"
        aria-label={t('preflight.matrix.title')}
        tabIndex={0}
      >
        <table className="a-tbl a-tbl--managed pfmatrix__table">
          <caption className="a-sr">{t('preflight.matrix.title')}</caption>
          <colgroup>
            <col className="pfmatrix__state" />
            {CATEGORIES.map((category) => (
              <col key={category} />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th scope="col">{t('preflight.matrix.state')}</th>
              {CATEGORIES.map((category) => (
                <th scope="col" key={category}>
                  {categoryLabel(category)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {groups.map(({ kind, rows }) => (
              <tr key={kind}>
                <th scope="row">
                  {t(`preflight.matrix.${kind}`)}
                  {rows === undefined ? null : (
                    <>
                      {' '}
                      <span className="pfmatrix__count">
                        {t('preflight.matrix.count', { count: rows.length })}
                      </span>
                    </>
                  )}
                </th>
                {rows === undefined ? (
                  <td colSpan={6} className="muted">
                    {t(`preflight.matrix.missing.${kind}`)}
                  </td>
                ) : (
                  CATEGORIES.map((category) => {
                    const entries = rows
                      .map((row, index) => ({ row, index }))
                      .filter(({ row }) => categoryOf(row) === category)
                    return (
                      <td key={category}>
                        {entries.length === 0 ? (
                          <EmptyCell checked={kind !== 'warning'} />
                        ) : (
                          <ul className="pfmatrix__items">
                            {entries.map(({ row, index }) => (
                              <li key={`${row.code}-${index}`}>
                                <MatrixItem
                                  row={row}
                                  rowExtra={rowExtra}
                                  warning={kind === 'warning' || kind === 'inconclusive'}
                                  settingPass={settingFor(row, passedBySetting)}
                                />
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    )
                  })
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function EmptyCell({ checked = false }: { checked?: boolean }) {
  return (
    <span className="pfmatrix__empty">
      {checked ? (
        <span className="pfmatrix__check" aria-hidden="true">
          ✓
        </span>
      ) : (
        <span aria-hidden="true">{t('mask.none')}</span>
      )}
      <span className="a-sr">{t('preflight.matrix.empty')}</span>
    </span>
  )
}

function MatrixItem({
  row,
  rowExtra,
  settingPass,
  warning = false,
}: {
  row: Row
  rowExtra?: PreflightRowExtraFn | undefined
  settingPass?: PreflightSettingPass | undefined
  warning?: boolean
}) {
  const extra = rowExtra?.(row)
  return (
    <>
      <div className="pfgroup__summary">
        <span className="pfgroup__problem">
          {warning ? (
            <span className="pfgroup__warning" aria-hidden="true">
              <i>▲</i>
            </span>
          ) : null}
          {problemOf(row)}
        </span>
        {settingPass === undefined ? null : (
          <span className="pfgroup__setting-pass">{t('preflight.group.settingPass')}</span>
        )}
        <Disclosure className="pfgroup__details" label={t('preflight.group.details')}>
          <RowDetails row={row} settingPass={settingPass} />
        </Disclosure>
      </div>
      <div className="pfgroup__actions">
        {extra?.actions}
        {extra?.after}
      </div>
    </>
  )
}

function RowDetails({
  row,
  settingPass,
}: {
  row: Row
  settingPass?: PreflightSettingPass | undefined
}) {
  const fields = [
    { key: 'code', value: row.code },
    { key: 'setting', value: settingPass?.setting },
    { key: 'module', value: row.module_id },
    { key: 'item', value: row.item },
    {
      key: 'verdict',
      value: 'verdict' in row ? enumLabel('enum.verdict', row.verdict) : undefined,
    },
    { key: 'detail', value: row.detail },
    { key: 'required', value: row.action_required },
    { key: 'confidence', value: 'confidence' in row ? row.confidence : undefined },
    { key: 'autoConfirm', value: 'auto_confirm_by' in row ? row.auto_confirm_by : undefined },
  ] as const
  return (
    <dl className="pfgroup__fields">
      {fields
        .filter((field) => field.value != null)
        .map((field) => (
          <div key={field.key}>
            <dt>{t(`preflight.group.${field.key}`)}</dt>
            <dd>{field.value}</dd>
          </div>
        ))}
    </dl>
  )
}
